// Comprehensive Offline-First Service for Agricverse
// Automatically caches ALL endpoints and syncs when online

import { authAPI } from './api';
import cacheService from './cacheService';
import networkUtils from '../utils/networkUtils';
import localforage from 'localforage';

class OfflineFirstService {
  constructor() {
    this.isInitialized = false;
    this.isCaching = false;
    this.syncInProgress = false;
    this.lastSyncTime = null;
    this.cachedEndpoints = new Set();
    
    // Online/offline status
    this.isOnline = navigator.onLine;
    this.onlineStatusCallbacks = new Set();
    
    // Storage for sync status and metadata
    this.syncStorage = localforage.createInstance({
      name: 'AgricverseApp',
      storeName: 'syncMetadata'
    });
    
    // All endpoints to cache (comprehensive list)
    this.allEndpoints = this.getAllEndpoints();
    
    // Initialize the service
    this.initialize();
  }

  // Get all API endpoints that should be cached
  getAllEndpoints() {
    return {
      // Authentication & Profile
      profile: [
        { method: 'getProfile', url: '/auth/profile', priority: 1 },
        { method: 'getUserProfile', url: '/profile', priority: 1 },
        { method: 'getNotificationPreferences', url: '/notification-preferences', priority: 3 },
        { method: 'getPrivacySettings', url: '/settings', priority: 3 }
      ],
      
      // Produce Management (Critical)
      produce: [
        { method: 'getProduce', url: '/auth/produce', priority: 1 },
        { method: 'getMyProduce', url: '/my_produce', priority: 1 },
        { method: 'getMyFarmProduce', url: '/my_produce', priority: 1 },
        { method: 'getCategories', url: '/categories', priority: 1 }
      ],
      
      // Products & Marketplace
      products: [
        { method: 'getProducts', url: '/products', priority: 2 }
      ],
      
      // Farm Management (Critical)
      farmManagement: [
        { method: 'getFarmManagement', url: '/manage/farm', priority: 1 }
      ],
      
      // Financial Data
      financial: [
        { method: 'getFarmerWallet', url: '/farmer/wallet', priority: 2 },
        { method: 'getFarmerOrders', url: '/farmer/orders', priority: 2 },
        { method: 'getFarmerWithdrawals', url: '/farmer/withdrawals', priority: 3 }
      ],
      
      // Communication
      communication: [
        { method: 'getConversations', url: '/conversations', priority: 3 }
      ],
      
      // Calendar/Events
      calendar: [
        { method: 'getCalendarEvents', url: '/calendar/events', priority: 3 }
      ],
      
      // Appointments
      appointments: [
        { method: 'getAppointments', url: '/appointments', priority: 3 }
      ]
    };
  }

  // Initialize the offline-first service
  async initialize() {
    if (this.isInitialized) return;
    
    try {
      // Check if user is authenticated before initializing
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        // Don't initialize if user is not logged in
        return;
      }
      
      // Set up network event listeners
      this.setupNetworkListeners();
      
      // Load previous sync status
      await this.loadSyncStatus();
      
      // If online, start comprehensive caching
      if (navigator.onLine) {
        this.startComprehensiveCaching();
      }
      
      this.isInitialized = true;
      
    } catch (error) {
      this.isInitialized = true; // Continue anyway
    }
  }

  // Set up network status listeners
  setupNetworkListeners() {
    window.addEventListener('online', async () => {
      this.isOnline = true;
      this.notifyOnlineStatusChange(true);
      
      // Show online notification
      this.showStatusNotification('You are back online!', 'success');
      
      // Start background sync and data refresh
      setTimeout(() => {
        this.performBackgroundSync();
        this.refreshAllCachedData();
      }, 1000); // Small delay to ensure connection is stable
    });
    
    window.addEventListener('offline', () => {
      this.isOnline = false;
      this.notifyOnlineStatusChange(false);
      
      // Show offline notification
      this.showStatusNotification('You are offline', 'warning');
    });
  }

  // Start comprehensive caching of ALL endpoints
  async startComprehensiveCaching() {
    if (this.isCaching) {
      return;
    }
    
    // Check token before starting
    const authToken = localStorage.getItem('authToken');
    if (!authToken) {
      this.isCaching = false;
      return;
    }
    
    this.isCaching = true;
    
    try {
      let totalEndpoints = 0;
      let cachedCount = 0;
      let failedCount = 0;
      
      // Count total endpoints
      Object.values(this.allEndpoints).forEach(category => {
        totalEndpoints += category.length;
      });
      
      // Cache by priority (1 = highest priority)
      for (let priority = 1; priority <= 3; priority++) {
        const priorityEndpoints = [];
        
        Object.entries(this.allEndpoints).forEach(([category, endpoints]) => {
          endpoints.forEach(endpoint => {
            if (endpoint.priority === priority) {
              priorityEndpoints.push({ ...endpoint, category });
            }
          });
        });
        
        // Cache in batches to avoid overwhelming the server
        const batchSize = 3;
        for (let i = 0; i < priorityEndpoints.length; i += batchSize) {
          // Check token before each batch
          const authToken = localStorage.getItem('authToken');
          if (!authToken) {
            this.isCaching = false;
            break; // Stop caching if token removed
          }
          
          const batch = priorityEndpoints.slice(i, i + batchSize);
          
          const results = await Promise.allSettled(
            batch.map(endpoint => this.cacheEndpoint(endpoint))
          );
          
          results.forEach((result, index) => {
            if (result.status === 'fulfilled' && result.value) {
              cachedCount++;
            } else {
              failedCount++;
            }
          });
          
          // Brief pause between batches to be gentle on the server
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      }
      
      // Update sync status
      await this.updateSyncStatus({
        lastSyncTime: Date.now(),
        totalEndpoints,
        cachedCount,
        failedCount,
        isComplete: true
      });
      
    } catch (error) {
      // Silent fail
    } finally {
      this.isCaching = false;
    }
  }

  // Cache a single endpoint
  async cacheEndpoint(endpoint) {
    // Check token before making request
    const authToken = localStorage.getItem('authToken');
    if (!authToken) {
      return false;
    }
    
    try {
      // Check if method exists in authAPI
      if (!authAPI[endpoint.method]) {
        return false;
      }
      
      // Call the API method
      const data = await authAPI[endpoint.method]();
      
      if (data) {
        this.cachedEndpoints.add(endpoint.url);
        return true;
      }
      
      return false;
    } catch (error) {
      // If 401, token is invalid - stop caching silently
      if (error.response?.status === 401 || error.response?.status === 403) {
        this.isCaching = false;
        return false;
      }
      
      return false;
    }
  }

  // Perform background sync when coming back online
  async performBackgroundSync() {
    if (this.syncInProgress) {
      return;
    }
    
    this.syncInProgress = true;
    
    try {
      // Sync offline queue first
      await cacheService.syncOfflineQueue();
      
      // Refresh all cached endpoints
      await this.refreshAllCachedData();
      
    } catch (error) {
      // Silent fail
    } finally {
      this.syncInProgress = false;
    }
  }

  // Refresh all cached data when online
  async refreshAllCachedData() {
    const authToken = localStorage.getItem('authToken');
    if (!authToken) return;
    
    try {
      // Refresh cached endpoints
      for (const [category, endpoints] of Object.entries(this.allEndpoints)) {
        for (const endpoint of endpoints) {
          if (this.cachedEndpoints.has(endpoint.url)) {
            try {
              if (authAPI[endpoint.method]) {
                await authAPI[endpoint.method]();
              }
            } catch (error) {
              // Silent fail
            }
          }
        }
        
        // Brief pause between categories
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
    } catch (error) {
      // Silent fail
    }
  }

  // Load sync status from storage
  async loadSyncStatus() {
    try {
      const syncStatus = await this.syncStorage.getItem('syncStatus');
      if (syncStatus) {
        this.lastSyncTime = syncStatus.lastSyncTime;
      }
    } catch (error) {
      // Silent fail
    }
  }

  // Update sync status in storage
  async updateSyncStatus(status) {
    try {
      await this.syncStorage.setItem('syncStatus', {
        ...status,
        timestamp: Date.now()
      });
      this.lastSyncTime = status.lastSyncTime;
    } catch (error) {
      // Silent fail
    }
  }

  // Show status notifications
  showStatusNotification(message, type = 'info') {
    // Create a custom event that components can listen to
    const event = new CustomEvent('offline-first-status', {
      detail: { message, type, timestamp: Date.now() }
    });
    window.dispatchEvent(event);
  }

  // Register callback for online status changes
  onOnlineStatusChange(callback) {
    this.onlineStatusCallbacks.add(callback);
    return () => this.onlineStatusCallbacks.delete(callback);
  }

  // Notify all callbacks about online status change
  notifyOnlineStatusChange(isOnline) {
    this.onlineStatusCallbacks.forEach(callback => {
      try {
        callback(isOnline);
      } catch (error) {
        // Silent fail
      }
    });
  }

  // Get service status
  getStatus() {
    return {
      isInitialized: this.isInitialized,
      isOnline: this.isOnline,
      isCaching: this.isCaching,
      syncInProgress: this.syncInProgress,
      lastSyncTime: this.lastSyncTime,
      cachedEndpointsCount: this.cachedEndpoints.size,
      totalEndpoints: Object.values(this.allEndpoints).reduce((sum, arr) => sum + arr.length, 0)
    };
  }

  // Force refresh all data (when user manually requests it)
  async forceRefreshAll() {
    if (!this.isOnline) {
      return false;
    }
    
    try {
      await this.startComprehensiveCaching();
      return true;
    } catch (error) {
      return false;
    }
  }

  // Clear all cached data (for troubleshooting)
  async clearAllCache() {
    try {
      // Clear all cache stores
      await cacheService.clearAllCache();
      
      // Clear our tracking
      this.cachedEndpoints.clear();
      await this.syncStorage.clear();
      
      // Re-cache if online
      if (this.isOnline) {
        setTimeout(() => this.startComprehensiveCaching(), 1000);
      }
      
    } catch (error) {
      // Silent fail
    }
  }
}

// Create singleton instance
const offlineFirstService = new OfflineFirstService();

// Export the singleton
export default offlineFirstService;
